import { useState } from 'react';
import type { AppSession, MeView, PracticeView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { Card } from '../../components/Bits';

interface MedRow {
  name: string;
  times: string;
  anticoagulant: boolean;
}

const DIAGNOSES = [
  { code: 'hypertension', label: 'Hypertension', focus: 'Blood pressure management' },
  { code: 'atrial-fibrillation', label: 'Atrial Fibrillation', focus: 'Heart rhythm monitoring' },
  { code: 'post-mi', label: 'Post-MI Recovery', focus: 'Recovery and secondary prevention' },
  { code: 'heart-failure', label: 'Heart Failure', focus: 'Fluid and symptom monitoring' },
  { code: 'cad', label: 'Coronary Artery Disease', focus: 'Risk factor control' },
] as const;

/** Prescribe HeartCoach: who, what they have, what they take, and what they are aiming for. */
export function EnrollPage({ session, me, practice, onChanged, say }: { session: AppSession; me: MeView; practice: PracticeView; onChanged: () => Promise<void>; say: (s: string) => void }) {
  const doctors = practice.roster.filter((r) => r.role === 'doctor');
  const [patient, setPatient] = useState('');
  const [name, setName] = useState('');
  const [age, setAge] = useState('');
  const [doctor, setDoctor] = useState(me.role === 'doctor' ? session.personId : (doctors[0]?.personId ?? ''));
  const [diagnoses, setDiagnoses] = useState<string[]>(['hypertension']);
  const [meds, setMeds] = useState<MedRow[]>([
    { name: 'Lisinopril 10mg', times: '08:00', anticoagulant: false },
    { name: 'Metoprolol 25mg', times: '08:00, 18:00', anticoagulant: false },
  ]);
  const [bp, setBp] = useState({ systolic: '130', diastolic: '80' });
  const [steps, setSteps] = useState('8000');
  const [adherence, setAdherence] = useState('95');
  const [tz, setTz] = useState(String(-new Date().getTimezoneOffset()));
  const [seedDemo, setSeedDemo] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const medications = meds
        .filter((m) => m.name.trim())
        .map((m) => ({
          id: m.name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
          name: m.name.trim(),
          times: m.times.split(/[,\s]+/).filter(Boolean),
          ...(m.anticoagulant ? { anticoagulant: true } : {}),
        }));
      const r = await api.enroll(
        {
          patient: patient.trim(),
          name: name.trim(),
          age: age.trim() ? Number(age) : null,
          diagnoses: DIAGNOSES.filter((d) => diagnoses.includes(d.code)).map((d) => ({ code: d.code, label: d.label, focus: d.focus })),
          medications,
          goals: [
            { kind: 'bp' as const, systolic: Number(bp.systolic), diastolic: Number(bp.diastolic) },
            { kind: 'steps' as const, target: Number(steps) },
            { kind: 'adherence' as const, percent: Number(adherence) },
          ],
          doctor: doctor || undefined,
          tzOffsetMinutes: Number(tz) || 0,
          seedDemo,
        },
        session.token,
      );
      say(`${name.trim()} enrolled under ${r.doctor}.`);
      await onChanged();
      location.hash = `#/practice/patients/${encodeURIComponent(r.patientId)}`;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <header className="desk-head">
        <div>
          <h1>Enroll a patient</h1>
          <span className="hint">Prescribe HeartCoach at the point of care. The patient sees this plan the first time they open the app.</span>
        </div>
      </header>
      <div className="two-col">
        <div className="stack">
          <Card>
            <h2>Who</h2>
            <label className="field">
              <span>Patient's Smart Agent address, agent name, or dev id</span>
              <input type="text" className="mono" value={patient} placeholder="0x… · sarah.me · dev:sarah-johnson" onChange={(e) => setPatient(e.target.value)} />
              <small className="hint">A dev id like <span className="mono">dev:sarah-johnson</span> matches the name typed at the development sign-in.</small>
            </label>
            <div className="pair">
              <label className="field">
                <span>Name</span>
                <input type="text" value={name} maxLength={60} onChange={(e) => setName(e.target.value)} />
              </label>
              <label className="field short">
                <span>Age</span>
                <input inputMode="numeric" value={age} maxLength={3} onChange={(e) => setAge(e.target.value.replace(/\D/g, ''))} />
              </label>
            </div>
            <label className="field">
              <span>Prescribing doctor</span>
              <select value={doctor} onChange={(e) => setDoctor(e.target.value)}>
                {doctors.length === 0 ? <option value="">No doctors on the roster</option> : null}
                {doctors.map((d) => (
                  <option key={d.personId} value={d.personId}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Patient's time zone (minutes east of UTC)</span>
              <input inputMode="numeric" value={tz} onChange={(e) => setTz(e.target.value.replace(/[^-\d]/g, ''))} />
            </label>
          </Card>
          <Card>
            <h2>Diagnoses</h2>
            <div className="chips">
              {DIAGNOSES.map((d) => (
                <button key={d.code} type="button" className={`chip ${diagnoses.includes(d.code) ? 'chip-on chip-red' : ''}`} aria-pressed={diagnoses.includes(d.code)} onClick={() => setDiagnoses(diagnoses.includes(d.code) ? diagnoses.filter((x) => x !== d.code) : [...diagnoses, d.code])}>
                  {d.label}
                </button>
              ))}
            </div>
          </Card>
        </div>
        <div className="stack">
          <Card>
            <h2>Medications</h2>
            {meds.map((m, i) => (
              <div className="med-row" key={i}>
                <input type="text" value={m.name} placeholder="Eliquis 5mg" onChange={(e) => setMeds(meds.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <input type="text" value={m.times} placeholder="08:00, 18:00" onChange={(e) => setMeds(meds.map((x, j) => (j === i ? { ...x, times: e.target.value } : x)))} />
                <label className="check">
                  <input type="checkbox" checked={m.anticoagulant} onChange={(e) => setMeds(meds.map((x, j) => (j === i ? { ...x, anticoagulant: e.target.checked } : x)))} /> thinner
                </label>
                <button type="button" className="quiet small" onClick={() => setMeds(meds.filter((_, j) => j !== i))} aria-label="Remove">
                  ×
                </button>
              </div>
            ))}
            <button type="button" className="secondary small" onClick={() => setMeds([...meds, { name: '', times: '08:00', anticoagulant: false }])}>
              + Add medication
            </button>
          </Card>
          <Card>
            <h2>Care goals</h2>
            <div className="pair">
              <label className="field short">
                <span>BP below</span>
                <input inputMode="numeric" value={bp.systolic} onChange={(e) => setBp({ ...bp, systolic: e.target.value.replace(/\D/g, '') })} />
              </label>
              <label className="field short">
                <span>/</span>
                <input inputMode="numeric" value={bp.diastolic} onChange={(e) => setBp({ ...bp, diastolic: e.target.value.replace(/\D/g, '') })} />
              </label>
              <label className="field short">
                <span>Steps daily</span>
                <input inputMode="numeric" value={steps} onChange={(e) => setSteps(e.target.value.replace(/\D/g, ''))} />
              </label>
              <label className="field short">
                <span>Adherence %</span>
                <input inputMode="numeric" value={adherence} onChange={(e) => setAdherence(e.target.value.replace(/\D/g, ''))} />
              </label>
            </div>
          </Card>
          <Card>
            <label className="check">
              <input type="checkbox" checked={seedDemo} onChange={(e) => setSeedDemo(e.target.checked)} /> Seed a fortnight of sample history (demo deployments only)
            </label>
            {error ? <p className="form-error">{error}</p> : null}
            <button type="button" className="primary big" disabled={busy || !patient.trim() || !name.trim() || !doctor} onClick={() => void submit()}>
              {busy ? 'Enrolling…' : 'Prescribe HeartCoach'}
            </button>
          </Card>
        </div>
      </div>
    </>
  );
}
